// Historical model runs: the rules for run requests, shared by historicalModelRunFunction (which runs them) and the
// web app (stage 4 previews, e.g. "this backfill runs as 24 chunks"). Pure functions only.
// See docs/plans/HISTORICAL_PROCESSED_MODELS_PLAN.md, § Stage 2 design.
//
// Keep this file dependency-free — the Lambda bundles it directly.

// ── Types ────────────────────────────────────────────────────────────────────────────────────────

export type RunTrigger = "CRON" | "MANUAL" | "BACKFILL" | "DATASET_UPLOAD" | "INPUT_FILE" | "MODEL_OUTPUT";
export type RunRequestStatus = "QUEUED" | "RUNNING" | "SUCCEEDED" | "FAILED" | "CANCELLED" | "MERGED" | "SPLIT";

/** Template `historicalConfig` merged with the model's `historicalSettings` (the model wins). */
export interface HistoricalRunConfig {
  /** First run without a watermark or backfillFrom covers [now − defaultLookback, now]. ISO 8601 duration. */
  defaultLookback?: string;
  /** Each scheduled run re-does this much before the watermark (late data). ISO 8601 duration. */
  overlap?: string;
  /** Longest range one run covers; longer requests are split. Whole months align to calendar months (UTC). */
  maxWindow?: string;
  /** Longest range a user may request. */
  maxRequestRange?: string;
  /** No run starts before this. */
  earliestStart?: string;
  /** First run starts here when the model has no watermark yet. */
  backfillFrom?: string;
  allowedTriggers?: RunTrigger[];
  /** Stage 3: how far a dataset-triggered run reaches back before the changed day, and how much extra the processor
   * gets on each side of its range to read (e.g. so a gap at the edge can be filled). ISO 8601 duration. */
  inputMargin?: string;
  /** Stage 3 (decision 2): how long a dataset-triggered request waits for more days of the same upload. 0 = none. */
  datasetSettleSeconds?: number;
  /** Stage 3 (decision 4): a model linked to a dataset processes its history automatically (default true). */
  autoBackfill?: boolean;
}

export interface RunRequestRow {
  id: string;
  status: RunRequestStatus;
  triggerType: RunTrigger;
  createdAt: string;
  windowStart?: string | null;
  windowEnd?: string | null;
  backfillId?: string | null;
  chunkIndex?: number | null;
  /** A retry waits until this time (decision 10, review of PR #940). */
  notBefore?: string | null;
  /** Function-only (IAM / Admin): set on every request this function or the executor made. A user can't set it. */
  triggerRef?: string | null;
  attempts?: number | null;
}

export interface TimeWindow {
  start: string;
  end: string;
}

export const DEFAULT_RUN_CONFIG: Required<
  Pick<HistoricalRunConfig, "defaultLookback" | "overlap" | "maxWindow" | "maxRequestRange">
> = {
  defaultLookback: "P1D",
  overlap: "PT0S",
  maxWindow: "P1M",
  maxRequestRange: "P5Y",
};

/** Attempts per request before it is FAILED (decision 10). */
export const MAX_RUN_ATTEMPTS = 3;
/** A lock outlives the run function's 15-minute limit by a minute, so a lock left by a dead run is known stale. */
export const LEASE_MS = 16 * 60_000;
/** A failed attempt's retry waits 1 minute, then 4. The 5-minute sweep restarts it once the wait is over. */
export const RETRY_DELAYS_MS = [60_000, 240_000];

/** When a request that just failed its `attempt`-th attempt may run again. */
export function retryNotBefore(attempt: number, now: string): string {
  const delay = RETRY_DELAYS_MS[Math.min(Math.max(attempt, 1), RETRY_DELAYS_MS.length) - 1];
  return toIso(Date.parse(now) + delay);
}

/** Stage 3 (decision 2): a dataset-upload request waits this long for more days of the same upload by default... */
export const DATASET_SETTLE_MS = 30_000;
/** ...but a group of them never waits longer than this after the first arrived, so a long rebuild makes progress. */
export const DATASET_MAX_WAIT_MS = 5 * 60_000;

/** One request never splits into more runs than this, nor into runs shorter than an hour. */
export const MAX_CHUNKS = 120;
const MIN_WINDOW_MS = 3_600_000;

// ── Times and durations ──────────────────────────────────────────────────────────────────────────

const ISO_Z = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?Z$/;
const DURATION = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?$/;

interface Duration {
  months: number;
  ms: number;
}

/** An ISO 8601 duration as calendar months plus fixed milliseconds. Throws on anything else. */
export function parseDuration(text: string): Duration {
  const match = DURATION.exec(text ?? "");
  if (!match || text === "P" || text.endsWith("T")) throw new Error(`"${text}" is not an ISO 8601 duration`);
  const [, y, mo, w, d, h, mi, s] = match.map((part) => Number(part ?? 0));
  return {
    months: y * 12 + mo,
    ms: ((w * 7 + d) * 24 * 3600 + h * 3600 + mi * 60 + s) * 1000,
  };
}

export function isIsoZ(value: unknown): value is string {
  return typeof value === "string" && ISO_Z.test(value) && !Number.isNaN(Date.parse(value));
}

/** Millisecond ISO with Z, the form every run row stores. */
export function toIso(time: number | string | Date): string {
  return new Date(time).toISOString();
}

export function addDuration(time: number, duration: Duration, sign: 1 | -1 = 1): number {
  const date = new Date(time);
  if (duration.months) {
    // Calendar months with the day of month clamped: Mar 31 − P1M is Feb 28, not Mar 3 (setUTCMonth overflows)
    const day = date.getUTCDate();
    date.setUTCDate(1);
    date.setUTCMonth(date.getUTCMonth() + sign * duration.months);
    date.setUTCDate(Math.min(day, new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate()));
  }
  return date.getTime() + sign * duration.ms;
}

const DURATION_KEYS = ["maxWindow", "defaultLookback", "maxRequestRange", "overlap", "inputMargin"] as const;

/**
 * Settings the run function cannot work with: a duration that doesn't parse (or is so long the date arithmetic
 * overflows), an earliestStart or backfillFrom that isn't a date. A request on such a model is refused with these;
 * before (review 2026-10-17) the run function threw after taking the lock, the request stayed QUEUED and the sweep
 * re-ran it into the same error every five minutes.
 */
export function configProblems(config: HistoricalRunConfig | null | undefined): string[] {
  const problems: string[] = [];
  for (const key of DURATION_KEYS) {
    const value = config?.[key];
    if (!value) continue;
    try {
      const duration = parseDuration(value);
      const moved = addDuration(Date.UTC(2000, 0, 1), duration);
      // A Date holds ±8.64e15 ms: past that, toIso throws (a huge month count already gives NaN)
      if (!Number.isFinite(moved) || Math.abs(moved) > 8.64e15) problems.push(`${key} "${value}" is too long`);
    } catch {
      problems.push(`${key} "${value}" is not an ISO 8601 duration`);
    }
  }
  for (const key of ["earliestStart", "backfillFrom"] as const) {
    const value = config?.[key];
    if (value && Number.isNaN(Date.parse(value))) problems.push(`${key} "${value}" is not a date`);
  }
  return problems;
}

function configValue(config: HistoricalRunConfig, key: keyof typeof DEFAULT_RUN_CONFIG): Duration {
  return parseDuration(config[key] || DEFAULT_RUN_CONFIG[key]);
}

// ── Windows ──────────────────────────────────────────────────────────────────────────────────────

/**
 * The config a run uses: the template's (set by admins), with only the model's backfillFrom and overlap taken from
 * its historicalSettings. The model owner can edit those settings, so the safety limits (maxWindow,
 * maxRequestRange, earliestStart, allowedTriggers) always come from the template.
 */
export function effectiveConfig(templateConfig: HistoricalRunConfig | null | undefined, modelSettings: HistoricalRunConfig | null | undefined): HistoricalRunConfig {
  const config: HistoricalRunConfig = { ...(templateConfig ?? {}) };
  if (modelSettings?.backfillFrom) config.backfillFrom = modelSettings.backfillFrom;
  if (modelSettings?.overlap) config.overlap = modelSettings.overlap;
  if (typeof modelSettings?.autoBackfill === "boolean") config.autoBackfill = modelSettings.autoBackfill;
  return config;
}

/**
 * The range a run covers, worked out when it starts (decision 5).
 * - A request with its own range keeps it.
 * - Otherwise [watermark − overlap, now]; without a watermark [backfillFrom, now], else [now − defaultLookback, now].
 * The start never goes before earliestStart, a requested one included (review 2026-10-17: a Re-run a range from
 * before the template's floor used to run as asked).
 */
export function windowFor(args: {
  now: string;
  config: HistoricalRunConfig;
  processedThrough?: string | null;
  requested?: Partial<TimeWindow> | null;
}): TimeWindow {
  const { now, config, processedThrough, requested } = args;
  const floor = config.earliestStart ? Date.parse(config.earliestStart) : NaN;
  if (requested?.start && requested?.end) {
    const start = Number.isNaN(floor) ? Date.parse(requested.start) : Math.max(Date.parse(requested.start), floor);
    return { start: toIso(start), end: toIso(requested.end) };
  }
  const end = Date.parse(now);
  let start: number;
  if (processedThrough) start = addDuration(Date.parse(processedThrough), configValue(config, "overlap"), -1);
  else if (config.backfillFrom) start = Date.parse(config.backfillFrom);
  else start = addDuration(end, configValue(config, "defaultLookback"), -1);
  if (!Number.isNaN(floor)) start = Math.max(start, floor);
  return { start: toIso(start), end: toIso(end) };
}

export type RangeProblemCode = "START_FORMAT" | "END_FORMAT" | "END_BEFORE_START" | "END_IN_FUTURE" | "TOO_LONG";

/** One problem with a user-requested range: a stable code, the message the run function stores, and the limit it hit. */
export interface RangeProblem {
  code: RangeProblemCode;
  message: string;
  /** The maxRequestRange that TOO_LONG compared against */
  limit?: string;
}

/** Problems with a user-requested range, with stable codes; empty when it can run. */
export function checkRangeProblems(window: Partial<TimeWindow>, now: string, config: HistoricalRunConfig): RangeProblem[] {
  const problems: RangeProblem[] = [];
  if (!isIsoZ(window.start)) problems.push({ code: "START_FORMAT", message: `Start "${window.start ?? ""}" is not an ISO 8601 UTC time ending in Z` });
  if (!isIsoZ(window.end)) problems.push({ code: "END_FORMAT", message: `End "${window.end ?? ""}" is not an ISO 8601 UTC time ending in Z` });
  if (problems.length) return problems;
  const start = Date.parse(window.start as string);
  const end = Date.parse(window.end as string);
  if (end <= start) problems.push({ code: "END_BEFORE_START", message: "End must be after start" });
  if (end > Date.parse(now)) problems.push({ code: "END_IN_FUTURE", message: "End is in the future" });
  const maxRange = config.maxRequestRange || DEFAULT_RUN_CONFIG.maxRequestRange;
  if (addDuration(start, parseDuration(maxRange)) < end) problems.push({ code: "TOO_LONG", message: `The range is longer than ${maxRange}`, limit: maxRange });
  return problems;
}

/** The same problems as messages only (what the run function stores on a refused request). */
export function checkRange(window: Partial<TimeWindow>, now: string, config: HistoricalRunConfig): string[] {
  return checkRangeProblems(window, now, config).map((p) => p.message);
}

/** A range problem in the words a form shows ("The range ends in the future", "The range is longer than 5 years"). */
export function describeRangeProblem(problem: RangeProblem): string {
  switch (problem.code) {
    case "END_IN_FUTURE":
      return "The range ends in the future";
    case "TOO_LONG":
      return `The range is longer than ${describeDurationText(problem.limit || DEFAULT_RUN_CONFIG.maxRequestRange)}`;
    default:
      return problem.message;
  }
}

/** "P5Y" → "5 years", "P1M" → "1 month", "PT6H" → "6 hours"; anything the form can't name stays as typed. */
export function describeDurationText(text: string): string {
  let d: Duration;
  try {
    d = parseDuration(text);
  } catch {
    return text;
  }
  const say = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;
  if (d.months && !d.ms) return d.months % 12 === 0 ? say(d.months / 12, "year") : say(d.months, "month");
  if (!d.months) {
    if (d.ms % 86_400_000 === 0) return say(d.ms / 86_400_000, "day");
    if (d.ms % 3_600_000 === 0) return say(d.ms / 3_600_000, "hour");
    if (d.ms % 60_000 === 0) return say(d.ms / 60_000, "minute");
  }
  return text;
}

/**
 * The earliest start a request may have (decisions 4 and 7): never more than maxRequestRange before now, and never
 * before the template's earliestStart. Used for a link's backfill, the re-run prompt and the wizard's history preview.
 */
export function capToMaxRange(start: string, now: string, config: HistoricalRunConfig): string {
  const end = Date.parse(now);
  let from = Date.parse(start);
  from = Math.max(from, addDuration(end, configValue(config, "maxRequestRange"), -1));
  if (config.earliestStart) from = Math.max(from, Date.parse(config.earliestStart));
  return toIso(from);
}

/**
 * Split a range into runs no longer than maxWindow (decisions 6–7). A whole-month maxWindow cuts at calendar
 * month starts (UTC), so 2024-10-15 → 2024-12-10 is [10-15, 11-01), [11-01, 12-01), [12-01, 12-10).
 */
export function splitWindow(window: TimeWindow, config: HistoricalRunConfig): TimeWindow[] {
  const step = configValue(config, "maxWindow");
  if (!step.months && step.ms < MIN_WINDOW_MS) throw new Error("maxWindow must be at least an hour");
  const end = Date.parse(window.end);
  const chunks: TimeWindow[] = [];
  let start = Date.parse(window.start);
  while (start < end) {
    let next: number;
    if (step.months && !step.ms) {
      const date = new Date(start);
      next = Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + step.months, 1);
    } else {
      next = addDuration(start, step);
    }
    next = Math.min(next, end);
    chunks.push({ start: toIso(start), end: toIso(next) });
    if (chunks.length > MAX_CHUNKS) throw new Error(`The range needs more than ${MAX_CHUNKS} runs of ${config.maxWindow || DEFAULT_RUN_CONFIG.maxWindow}`);
    start = next;
  }
  return chunks;
}

// ── Ids ──────────────────────────────────────────────────────────────────────────────────────────

/** A scheduled run's request id. The poller sets lastRunAt when it claims the schedule, so the executor's
 * automatic retries make the same id and the second create fails harmlessly (decision 2). */
export function cronRequestId(scheduleId: string, lastRunAt: string): string {
  return `cron-${scheduleId}-${lastRunAt}`;
}

export function chunkId(parentId: string, index: number, count: number): string {
  return `${parentId}-c${String(index).padStart(Math.max(2, String(count).length), "0")}`;
}

/** One upload per output per run: `<runId>-<n>` (ingest accepts 8–64 letters, digits or dashes). */
export function uploadIdFor(runId: string, index: number): string {
  return `${runId}-${index + 1}`;
}

// ── Data runners: an output that writes a dataset (storageMode DATASET) ─────────────────────────────

/** An output whose runs write into a dataset (targetDatasetId): every HISTORICAL output since phase 2. */
export const isDatasetOutput = (output: { storageMode?: string | null } | null | undefined): boolean =>
  output?.storageMode === "DATASET";

/** Where a DATASET output's file goes: the target dataset's incoming/ folder, which datasetIngestFunction watches. */
export function datasetIncomingKey(routeLong: string, uploadId: string): string {
  return `${routeLong.replace(/\/+$/, "")}/incoming/${uploadId}.csv`;
}

/**
 * S3 metadata for a DATASET output's file. run-id / inputs-read-at keep the run's lineage on the object; uploadid,
 * uploadedby and originalfilename are what datasetIngestFunction reads (the run function creates the DatasetUpload
 * row first, so ingest finds it rather than making one).
 */
export function datasetUploadMetadata(args: {
  runId: string;
  inputsReadAt: string;
  uploadId: string;
  ownerSub?: string | null;
  fileName: string;
}): Record<string, string> {
  return {
    "run-id": args.runId,
    "inputs-read-at": args.inputsReadAt,
    uploadid: args.uploadId,
    ...(args.ownerSub ? { uploadedby: args.ownerSub } : {}),
    originalfilename: encodeURIComponent(args.fileName),
    source: "MODEL",
  };
}

export interface DatasetWriteRow extends DatasetAccessRow {
  id?: string;
  routeLong?: string | null;
  fileFormat?: string | null;
}

/** A dataset's storage folder is its own: `{private|organizationprivate}/<owner>/datasets/<id>/`, the org one under its
 * own organization. routeLong is owner-editable, so a run never writes anywhere it points without this check. */
export function datasetRouteIsOwn(dataset: Pick<DatasetWriteRow, "id" | "routeLong" | "organizationId"> | null | undefined): boolean {
  const m = /^(private|organizationprivate)\/([^/]+)\/datasets\/([^/]+)\/$/.exec(dataset?.routeLong ?? "");
  if (!m || !dataset?.id || m[3] !== dataset.id) return false;
  return m[1] === "private" ? !dataset.organizationId : m[2] === dataset.organizationId;
}

/** Why a dataset can't take a model's rows whatever the owner (curation dedupes overlapping runs; ingest reads CSV), or null. */
export function datasetWriteTargetProblem(dataset: Pick<DatasetWriteRow, "datasetType" | "curationMode" | "fileFormat"> | null | undefined): string | null {
  if (!dataset) return "The target dataset no longer exists";
  if (dataset.datasetType !== "HISTORICAL") return "A model output can only be written to a historical dataset";
  if (dataset.curationMode !== "PARQUET") {
    return "The dataset isn't curated, so overlapping runs would duplicate rows: turn curation on first";
  }
  if (dataset.fileFormat && dataset.fileFormat !== "CSV") return "The dataset takes only CSV files";
  return null;
}

/**
 * May a model write its output into this dataset? Its owner must be able to upload to the dataset in the app (own it,
 * be in its organization, or be an Admin — AdminReadOnly can't write), the dataset must be HISTORICAL, and the same
 * cross-organization rule as reading applies (canModelReadDataset). A model can't write a dataset it reads (each run's
 * upload would queue the next run). Lambda and HTTPS processors alike: a Lambda processor writes the dataset's
 * incoming/ key itself (its role is granted it), an HTTPS one answers and the run function writes. Re-checked
 * before every run.
 */
export function canModelWriteDataset(
  model: { owner?: string | null; organizationId?: string | null },
  ownerIdentity: RequesterIdentity | null,
  dataset: DatasetWriteRow | null | undefined,
  opts: { inputDatasetIds?: string[] } = {}
): { ok: boolean; reason?: string; crossOrg: boolean } {
  if (!dataset || dataset.deletedAt) return { ok: false, reason: "The target dataset no longer exists", crossOrg: false };
  if (!datasetRouteIsOwn(dataset)) return { ok: false, reason: "The target dataset's storage folder isn't its own", crossOrg: false };
  if (!ownerIdentity) return { ok: false, reason: "The model's owner could not be found", crossOrg: false };
  const targetProblem = datasetWriteTargetProblem(dataset);
  if (targetProblem) return { ok: false, reason: targetProblem, crossOrg: false };
  if (dataset.id && (opts.inputDatasetIds ?? []).includes(dataset.id)) {
    return { ok: false, reason: "A model can't write to a dataset it reads", crossOrg: false };
  }
  const ownsDataset = ownerMatches(ownerIdentity, dataset.owner);
  const isAdmin = ownerIdentity.groups.includes("Admin");
  const writable =
    ownsDataset || isAdmin || (Boolean(dataset.organizationId) && ownerIdentity.groups.includes(dataset.organizationId as string));
  if (!writable) return { ok: false, reason: "The model's owner can't upload to this dataset", crossOrg: false };
  const crossOrg = model.organizationId ? model.organizationId !== dataset.organizationId : !ownsDataset;
  if (crossOrg && !isAdmin) {
    return {
      ok: false,
      reason: model.organizationId
        ? "The dataset isn't in the model's organization; only an Admin can write another organization's dataset"
        : "A personal model can only write its owner's own datasets",
      crossOrg,
    };
  }
  return { ok: true, crossOrg };
}

/**
 * What went wrong with a DATASET output's upload once it settled, if anything. An empty window ("No data rows after
 * the header": the source had nothing new yet) is not a failure — the run succeeds and the next one covers it.
 */
export function datasetUploadProblems(
  row: { status?: string | null; validationErrors?: string[] | null; failureReason?: string | null } | null | undefined,
  label: string,
  waitedSeconds: number
): string[] {
  if (!row) return [`No upload row for output ${label}`];
  if (row.status === "LOADED") return [];
  if (row.status === "REJECTED") {
    const errors = row.validationErrors ?? [];
    if (errors.length && errors.every((e) => /^No data rows after the header/.test(e))) return [];
    return [`Output ${label} was rejected by its dataset: ${errors.join("; ")}`];
  }
  if (row.status === "FAILED") return [`Output ${label} failed to load into its dataset: ${row.failureReason ?? "unknown error"}`];
  return [`Output ${label}'s dataset upload was still ${row.status ?? "REQUESTED"} after ${waitedSeconds} s`];
}

/** Whether a DATASET output's upload has settled (ingest accepted or rejected it). */
export const datasetUploadSettled = (row: { status?: string | null } | null | undefined): boolean =>
  ["LOADED", "REJECTED", "FAILED"].includes(row?.status ?? "");

// ── Picking the next request ─────────────────────────────────────────────────────────────────────

const IN_FLIGHT: RunRequestStatus[] = ["QUEUED", "RUNNING", "SPLIT"];

function queueOrder(a: RunRequestRow, b: RunRequestRow): number {
  return (
    a.createdAt.localeCompare(b.createdAt) ||
    (a.chunkIndex ?? 0) - (b.chunkIndex ?? 0) ||
    a.id.localeCompare(b.id)
  );
}

function sameRange(a: RunRequestRow, b: RunRequestRow): boolean {
  return Boolean(a.windowStart && a.windowEnd) && a.windowStart === b.windowStart && a.windowEnd === b.windowEnd;
}

/**
 * The model's next request, and the waiting requests that merge into another (decisions 8–9). Called while the
 * function holds the model's lock, so two invocations can't both decide.
 * - First come, first served: oldest createdAt; chunks (which carry their backfill's createdAt) by chunkIndex.
 * - A BACKFILL with the same range as an earlier one still in flight merges into it.
 * - A picked scheduled (CRON) request absorbs every other waiting one (their range is worked out at run start).
 *   Chunks of a split scheduled request are never merged, nor merged into: each covers its own piece.
 * - Other manual requests are never merged.
 * - A request still waiting out a retry delay (notBefore after `now`) is passed over; the next one runs.
 * - Waiting dataset-upload requests (stage 3, decision 2) are one group. The group is due once the newest one's
 *   notBefore has passed (each new day pushes it back), but never later than DATASET_MAX_WAIT_MS after the oldest was
 *   created. When it runs, the oldest absorbs the rest and its range becomes all of theirs (`pickWindow`). Until
 *   then `waitUntil` says when it's due.
 */
export function pickNext(requests: RunRequestRow[], now?: string): {
  pick: RunRequestRow | null;
  merges: { id: string; into: string }[];
  pickWindow?: TimeWindow;
  waitUntil?: string;
} {
  const ordered = [...requests].sort(queueOrder);
  const merges: { id: string; into: string }[] = [];
  const merged = new Set<string>();
  const group = ordered.filter(isUpdateGroupMember);
  const groupDue = datasetGroupDue(group);

  ordered.forEach((request, position) => {
    if (request.status !== "QUEUED" || request.triggerType !== "BACKFILL" || request.backfillId) return;
    const original = ordered.find(
      (other, otherPosition) =>
        other.id !== request.id &&
        other.triggerType === "BACKFILL" &&
        !other.backfillId &&
        !merged.has(other.id) &&
        sameRange(other, request) &&
        (other.status === "RUNNING" || other.status === "SPLIT" || (other.status === "QUEUED" && otherPosition < position))
    );
    if (original) {
      merges.push({ id: request.id, into: original.id });
      merged.add(request.id);
    }
  });

  const ready = (request: RunRequestRow) => {
    if (!now) return true;
    if (isUpdateGroupMember(request)) return groupDue !== null && groupDue <= Date.parse(now);
    return !request.notBefore || Date.parse(request.notBefore) <= Date.parse(now);
  };
  const pick = ordered.find((request) => request.status === "QUEUED" && !merged.has(request.id) && ready(request)) ?? null;
  if (pick?.triggerType === "CRON" && !pick.backfillId) {
    for (const request of ordered) {
      if (request.id !== pick.id && request.status === "QUEUED" && request.triggerType === "CRON" && !request.backfillId) {
        merges.push({ id: request.id, into: pick.id });
      }
    }
  }
  let pickWindow: TimeWindow | undefined;
  if (pick && isUpdateGroupMember(pick)) {
    for (const request of group) if (request.id !== pick.id) merges.push({ id: request.id, into: pick.id });
    pickWindow = unionWindow(group);
  }
  const waitUntil =
    groupDue !== null && (!now || groupDue > Date.parse(now)) && !(pick && isUpdateGroupMember(pick)) ? toIso(groupDue) : undefined;
  return { pick, merges, ...(pickWindow ? { pickWindow } : {}), ...(waitUntil ? { waitUntil } : {}) };
}

/** An update-driven request (a dataset day built, or an upstream model's output day built) still waiting, not a chunk:
 * these are combined into one run, so an upstream backfill's thirty day builds become one downstream run. Only requests
 * this function made count (triggerRef is function-only): a user-made DATASET_UPLOAD / MODEL_OUTPUT row stands alone
 * and is refused as before, so it can neither swallow the real requests nor widen their range. */
function isUpdateGroupMember(request: RunRequestRow): boolean {
  return (
    request.status === "QUEUED" &&
    (request.triggerType === "DATASET_UPLOAD" || request.triggerType === "MODEL_OUTPUT") &&
    !request.backfillId &&
    Boolean(request.triggerRef)
  );
}

/** When the group may run: the newest member's notBefore, capped at the oldest's createdAt + DATASET_MAX_WAIT_MS. A
 * member waiting out a retry delay (attempts > 0) keeps that delay: the cap only limits the settle wait. */
function datasetGroupDue(group: RunRequestRow[]): number | null {
  if (!group.length) return null;
  const latest = Math.max(...group.map((r) => Date.parse(r.notBefore || r.createdAt)));
  const cap = Math.min(...group.map((r) => Date.parse(r.createdAt))) + DATASET_MAX_WAIT_MS;
  const retries = group.filter((r) => (r.attempts ?? 0) > 0 && r.notBefore).map((r) => Date.parse(r.notBefore as string));
  return Math.max(Math.min(latest, cap), ...retries);
}

/** Earliest start to latest end over the requests that have a range. */
function unionWindow(requests: RunRequestRow[]): TimeWindow | undefined {
  const ranged = requests.filter((r) => r.windowStart && r.windowEnd);
  if (!ranged.length) return undefined;
  return {
    start: toIso(Math.min(...ranged.map((r) => Date.parse(r.windowStart as string)))),
    end: toIso(Math.max(...ranged.map((r) => Date.parse(r.windowEnd as string)))),
  };
}

/** A split request's outcome once its chunks are done: null while any is waiting or running. */
export function parentOutcome(chunks: Pick<RunRequestRow, "status">[]): "SUCCEEDED" | "FAILED" | null {
  if (!chunks.length || chunks.some((chunk) => IN_FLIGHT.includes(chunk.status))) return null;
  return chunks.some((chunk) => chunk.status === "FAILED" || chunk.status === "CANCELLED") ? "FAILED" : "SUCCEEDED";
}

/** The watermark only moves forward (decision 12: and only after a run succeeds). */
export function nextWatermark(current: string | null | undefined, windowEnd: string): string {
  return current && Date.parse(current) >= Date.parse(windowEnd) ? current : toIso(windowEnd);
}

// ── The model's lock (ProcessedModelRunState) ────────────────────────────────────────────────────

export interface LeaseState {
  leaseHolder?: string | null;
  leaseUntil?: string | null;
}

/** Free, or held by a run that is past its time (a dead run). */
export function canTakeLease(state: LeaseState | null | undefined, now: string): boolean {
  return !state?.leaseUntil || Date.parse(state.leaseUntil) < Date.parse(now);
}

export function canReleaseLease(state: LeaseState | null | undefined, holder: string): boolean {
  return state?.leaseHolder === holder;
}

/** DynamoDB update for taking the lock. Its condition is canTakeLease. */
export function takeLeaseUpdate(modelId: string, holder: string, now: string) {
  return {
    Key: { id: modelId },
    UpdateExpression: "SET leaseHolder = :holder, leaseUntil = :until, updatedAt = :now, createdAt = if_not_exists(createdAt, :now), #typename = :typename",
    ConditionExpression: "attribute_not_exists(leaseUntil) OR leaseUntil < :now",
    ExpressionAttributeNames: { "#typename": "__typename" },
    ExpressionAttributeValues: {
      ":holder": holder,
      ":until": toIso(Date.parse(now) + LEASE_MS),
      ":now": now,
      ":typename": "ProcessedModelRunState",
    },
  };
}

/** DynamoDB update for releasing the lock. Its condition is canReleaseLease. */
export function releaseLeaseUpdate(modelId: string, holder: string, now: string) {
  return {
    Key: { id: modelId },
    UpdateExpression: "REMOVE leaseHolder, leaseUntil, leaseRequestId, leaseRunId SET updatedAt = :now",
    ConditionExpression: "leaseHolder = :holder",
    ExpressionAttributeValues: { ":holder": holder, ":now": now },
  };
}

// ── Processors and access ────────────────────────────────────────────────────────────────────────

const PROCESSOR_LAMBDA = /^lambda:(historicalProcessor[A-Z][A-Za-z0-9]*)$/;

export type ProcessorTarget = { kind: "lambda"; functionName: string } | { kind: "https"; url: string };

/** A template's executionUrl → what the run function may call; null when it isn't allowed. Lambda processors are
 * only ever historicalProcessor<Name>-<env>, which is also all the run function's IAM allows. */
export function resolveProcessor(executionUrl: string | null | undefined, envId: string): ProcessorTarget | null {
  const lambdaMatch = PROCESSOR_LAMBDA.exec(executionUrl ?? "");
  if (lambdaMatch) return { kind: "lambda", functionName: `${lambdaMatch[1]}-${envId}` };
  if (/^https:\/\/[^\s/]+/.test(executionUrl ?? "")) return { kind: "https", url: executionUrl as string };
  return null;
}

export interface RequesterIdentity {
  sub: string;
  username: string;
  groups: string[];
}

/** Who may run a model: its owner (any of the three stored owner formats) and Admins (decision 4). */
export function canRequestRun(requester: RequesterIdentity | null, modelOwner: string | null | undefined): boolean {
  if (!requester) return false;
  if (requester.groups.includes("Admin")) return true;
  const candidates = new Set([requester.sub, requester.username, `${requester.sub}::${requester.username}`]);
  return Boolean(modelOwner) && candidates.has(modelOwner as string);
}

// ── HTTPS processors (decision 16) ───────────────────────────────────────────────────────────────

export interface IssuedOutput {
  parameterTypeId?: string | null;
  s3Key: string;
}

/**
 * An HTTPS processor's response → the file each issued output gets. A `text/csv` body is the one output's file (a
 * model with several outputs must answer JSON). A JSON body maps parameterTypeId → CSV text. Throws with the
 * reason when the answer doesn't cover every output.
 */
export function filesFromProcessorResponse(
  contentType: string | null | undefined,
  text: string,
  outputs: IssuedOutput[]
): { s3Key: string; body: string }[] {
  if (/^text\/csv/i.test(contentType ?? "")) {
    if (outputs.length !== 1) throw new Error(`The processor answered one CSV for ${outputs.length} outputs; answer JSON keyed by parameterTypeId`);
    return [{ s3Key: outputs[0].s3Key, body: text }];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    throw new Error(`The processor answered neither CSV nor JSON: ${text.slice(0, 200)}`);
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("The processor's JSON answer must map parameterTypeId to CSV text");
  }
  const byParameter = parsed as Record<string, unknown>;
  return outputs.map((output) => {
    const body = output.parameterTypeId ? byParameter[output.parameterTypeId] : undefined;
    if (typeof body !== "string") throw new Error(`The processor's answer has no CSV for "${output.parameterTypeId}"`);
    return { s3Key: output.s3Key, body };
  });
}

// ── Stage 3: dataset inputs ──────────────────────────────────────────────────────────────────────
// A model reads a curated HISTORICAL dataset it is linked to (ProcessedModelSchemaDatasets), and a dataset day being
// built queues a run of every model linked to it. See § Stage 3 design.

const DAY_MS = 86_400_000;

/** The template's `dataset` field, as fieldDefinitions declares it. */
export interface DatasetFieldValue {
  type?: string;
  datasetId?: string | null;
  columns?: string[] | null;
  triggerOnUpload?: boolean | null;
}

/** A link row ("model M reads dataset D through field F"), written only by historicalModelRunFunction. */
export interface DatasetLinkRow {
  id: string;
  processedModelSchemaId: string;
  fieldKey: string;
  datasetId: string;
  columns: string[];
  triggerOnUpload: boolean;
  status?: DatasetLinkStatus;
  statusReason?: string | null;
  crossOrg?: boolean | null;
  /** How many link backfills were queued (their ids count up). */
  backfills?: number | null;
}

export type DatasetLinkStatus = "ACTIVE" | "REFUSED" | "NOT_CURATED";

export const datasetLinkId = (modelId: string, fieldKey: string) => `${modelId}#${fieldKey}`;
/** A dataset build's request for one model: a retried stream record makes the same id (a harmless duplicate). */
export const datasetRequestId = (buildRunId: string, modelId: string) => `ds-${buildRunId}-${modelId}`;
/** An upstream output's day build (or forecast issue) request for one downstream model. */
export const outputRequestId = (buildRunId: string, modelId: string) => `mo-${buildRunId}-${modelId}`;
/** The n-th backfill a link queued (decision 4). */
export const linkBackfillId = (modelId: string, fieldKey: string, n: number) => `link-${modelId}-${fieldKey}-${n}`;

/**
 * The links a model should have: one per `dataset` field of its template that the model has filled in. Only
 * HISTORICAL models read datasets this way.
 */
export function datasetLinksFor(
  model: { id: string; modelKind?: string | null; schemaFields?: Record<string, unknown> | null },
  fieldDefinitions: Record<string, unknown> | null | undefined
): Omit<DatasetLinkRow, "status" | "statusReason" | "crossOrg" | "backfills">[] {
  if (model.modelKind !== "HISTORICAL") return [];
  const links: Omit<DatasetLinkRow, "status" | "statusReason" | "crossOrg" | "backfills">[] = [];
  for (const [fieldKey, definition] of Object.entries(fieldDefinitions ?? {})) {
    if ((definition as { type?: string } | null)?.type !== "dataset") continue;
    const value = model.schemaFields?.[fieldKey] as DatasetFieldValue | null | undefined;
    if (!value || typeof value !== "object" || !value.datasetId) continue;
    links.push({
      id: datasetLinkId(model.id, fieldKey),
      processedModelSchemaId: model.id,
      fieldKey,
      datasetId: String(value.datasetId),
      columns: Array.isArray(value.columns) ? value.columns.map(String) : [],
      triggerOnUpload: value.triggerOnUpload !== false,
    });
  }
  return links;
}

export interface DatasetAccessRow {
  owner?: string | null;
  organizationId?: string | null;
  datasetType?: string | null;
  curationMode?: string | null;
  deletedAt?: string | null;
}

/** An owner field (sub, username or sub::username) names this person. */
export function ownerMatches(identity: RequesterIdentity, owner: string | null | undefined): boolean {
  if (!owner) return false;
  const names = new Set([identity.sub, identity.username]);
  return owner === `${identity.sub}::${identity.username}` || owner.split("::").some((part) => names.has(part));
}

/**
 * May a model read this dataset (decision 5)? Yes when the model's owner can read it in the app: they own it, are in
 * its organization, or are an Admin. A link outside the model's own organization (or, for a personal model, outside
 * its owner's own datasets) is crossOrg: allowed for an Admin owner (the form and model page warn), refused for
 * anyone else, so no path exists from one organization's dataset to another's models except through an Admin.
 * Re-checked before every run.
 */
export function canModelReadDataset(
  model: { owner?: string | null; organizationId?: string | null },
  ownerIdentity: RequesterIdentity | null,
  dataset: DatasetAccessRow | null | undefined
): { status: DatasetLinkStatus; reason?: string; crossOrg: boolean } {
  if (!dataset || dataset.deletedAt) return { status: "REFUSED", reason: "The dataset no longer exists", crossOrg: false };
  if (!ownerIdentity) return { status: "REFUSED", reason: "The model's owner could not be found", crossOrg: false };
  if (dataset.datasetType !== "HISTORICAL") {
    return { status: "REFUSED", reason: "Only historical datasets can be a historical model's input", crossOrg: false };
  }
  const ownsDataset = ownerMatches(ownerIdentity, dataset.owner);
  const readable =
    ownsDataset ||
    ownerIdentity.groups.includes("Admin") ||
    ownerIdentity.groups.includes("AdminReadOnly") ||
    (Boolean(dataset.organizationId) && ownerIdentity.groups.includes(dataset.organizationId as string));
  if (!readable) return { status: "REFUSED", reason: "The model's owner can't read this dataset", crossOrg: false };
  const crossOrg = model.organizationId ? model.organizationId !== dataset.organizationId : !ownsDataset;
  if (crossOrg && !ownerIdentity.groups.includes("Admin")) {
    return {
      status: "REFUSED",
      reason: model.organizationId
        ? "The dataset isn't in the model's organization; only an Admin can link another organization's dataset"
        : "A personal model can only read its owner's own datasets",
      crossOrg,
    };
  }
  if (dataset.curationMode !== "PARQUET") {
    return { status: "NOT_CURATED", reason: "The dataset isn't curated: turn curation on and rebuild its history", crossOrg };
  }
  return { status: "ACTIVE", crossOrg };
}

/** A DatasetProcessingRun change that means "a day's curated file is now live": a day build (an upload's, or an
 * admin rebuild's; decision 1) that went RUNNING → SUCCEEDED. Month merges and cleanup change no values. */
export function isDayBuilt(next: { type?: string; status?: string } | null | undefined, previousStatus?: string | null): boolean {
  return next?.type === "DAY_BUILD" && next.status === "SUCCEEDED" && previousStatus === "RUNNING";
}

/** `time` moved by the template's inputMargin (any ISO 8601 duration, months included; none = 0). */
function withMargin(time: number, margin: string | null | undefined, sign: 1 | -1): number {
  return margin ? addDuration(time, parseDuration(margin), sign) : time;
}

/**
 * The run request a built dataset day queues for one linked model, or null when it queues none: the range is the
 * day, reaching back by the template's inputMargin (so a gap across midnight is filled once the next day arrives)
 * and never past now. It may not start for datasetSettleSeconds, so more days of the same upload join it.
 */
export function datasetRequestFor(args: {
  buildRunId: string;
  period: string;
  link: Pick<DatasetLinkRow, "processedModelSchemaId" | "status" | "triggerOnUpload">;
  config: HistoricalRunConfig;
  now: string;
  /** Stage 4 (decision 8): an inactive model is paused, so uploads don't start runs (Run now and backfills still do) */
  modelActive?: boolean;
  /** What built the day: a dataset upload (default) or an upstream model's output (a `model_output` link). */
  trigger?: "DATASET_UPLOAD" | "MODEL_OUTPUT";
}): { id: string; windowStart: string; windowEnd: string; notBefore: string; triggerRef: string } | null {
  const { buildRunId, period, link, config, now, modelActive = true, trigger = "DATASET_UPLOAD" } = args;
  if (!modelActive) return null;
  if (link.status !== "ACTIVE" || !link.triggerOnUpload) return null;
  if (config.allowedTriggers?.length && !config.allowedTriggers.includes(trigger)) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(period)) return null;
  const dayStart = Date.parse(`${period}T00:00:00.000Z`);
  const nowMs = Date.parse(now);
  if (Number.isNaN(dayStart) || dayStart >= nowMs) return null;
  let start = withMargin(dayStart, config.inputMargin, -1);
  if (config.earliestStart) start = Math.max(start, Date.parse(config.earliestStart));
  const end = Math.min(dayStart + DAY_MS, nowMs);
  if (end <= start) return null;
  const settleMs = (config.datasetSettleSeconds ?? DATASET_SETTLE_MS / 1000) * 1000;
  return {
    id: trigger === "MODEL_OUTPUT" ? outputRequestId(buildRunId, link.processedModelSchemaId) : datasetRequestId(buildRunId, link.processedModelSchemaId),
    windowStart: toIso(start),
    windowEnd: toIso(end),
    notBefore: toIso(nowMs + settleMs),
    triggerRef: buildRunId,
  };
}

/**
 * The range a new link's backfill covers (decision 4), or null for none: from the model's backfillFrom, else the
 * dataset's first curated day, to now; never longer than maxRequestRange nor before earliestStart.
 */
export function linkBackfillWindow(args: {
  config: HistoricalRunConfig;
  firstPeriodStart: string | null | undefined;
  now: string;
}): TimeWindow | null {
  const { config, firstPeriodStart, now } = args;
  if (config.autoBackfill === false) return null;
  if (config.allowedTriggers?.length && !config.allowedTriggers.includes("BACKFILL")) return null;
  const from = config.backfillFrom || firstPeriodStart;
  if (!from || Number.isNaN(Date.parse(from))) return null;
  const end = Date.parse(now);
  const start = Date.parse(capToMaxRange(from, now, config));
  return start < end ? { start: toIso(start), end: toIso(end) } : null;
}

/** The input range a run reads: its own range plus inputMargin on each side. */
export function inputWindow(window: TimeWindow, config: HistoricalRunConfig): TimeWindow {
  return {
    start: toIso(withMargin(Date.parse(window.start), config.inputMargin, -1)),
    end: toIso(withMargin(Date.parse(window.end), config.inputMargin, 1)),
  };
}

/** A DatasetFile catalog row. */
export interface DatasetFileRow {
  id: string;
  granularity: "DAY" | "MONTH" | string;
  periodStart: string;
  periodEnd: string;
  s3Key: string;
  version: number;
  active: boolean;
}

export interface InputFile {
  fileId: string;
  s3Key: string;
  granularity: string;
  periodStart: string;
  periodEnd: string;
  version: number;
  /** Ranges of a month file that a day file overrides: read those days from the day file instead. */
  excludeRanges: [string, string][];
}

/**
 * The dataset files a run reads for a range (the dataset query's rule, datasetQueryFunction active_files): live
 * versions only, overlapping the range, in time order. A day rebuilt after its month was merged has its own live day
 * file, which wins over the month file for that day. Throws if a key is outside the dataset's curated folder.
 */
export function activeInputFiles(rows: DatasetFileRow[], window: TimeWindow, curatedPrefix: string): InputFile[] {
  const start = Date.parse(window.start);
  const end = Date.parse(window.end);
  const live = rows
    .filter((r) => r.active && Date.parse(r.periodEnd) > start && Date.parse(r.periodStart) < end)
    .sort((a, b) => Date.parse(a.periodStart) - Date.parse(b.periodStart) || (a.granularity === "MONTH" ? -1 : 1));
  for (const row of live) {
    if (!curatedPrefix || !row.s3Key.startsWith(curatedPrefix)) {
      throw new Error(`Input file ${row.id} is outside its source's curated folder (${row.s3Key})`);
    }
  }
  const days = live.filter((r) => r.granularity === "DAY");
  return live.map((row) => ({
    fileId: row.id,
    s3Key: row.s3Key,
    granularity: row.granularity,
    periodStart: toIso(row.periodStart),
    periodEnd: toIso(row.periodEnd),
    version: row.version,
    excludeRanges:
      row.granularity === "MONTH"
        ? days
            .filter((d) => Date.parse(d.periodStart) >= Date.parse(row.periodStart) && Date.parse(d.periodEnd) <= Date.parse(row.periodEnd))
            .map((d) => [toIso(d.periodStart), toIso(d.periodEnd)] as [string, string])
        : [],
  }));
}

// ── Model outputs as inputs (docs/architecture/MODEL_OUTPUT_INPUTS.md) ───────────────────────────
// A template's `model_output` field links a model to another model's output param, the way a `dataset` field links a
// dataset: one ProcessedModelSchemaOutputs row per (model, field), written by historicalModelRunFunction, access
// checked when written and before each run. Only a FILES (forecast) output can be linked: its issues are handed to the
// processor as files to read. A HISTORICAL model's data lives in the dataset it writes (link that with a dataset field).

export type OutputStorageMode = "FILES";

/** The template's `model_output` field value, as the wizard stores it. */
export interface ModelOutputFieldValue {
  type?: string;
  outputParamId?: string | null;
  modelId?: string | null;
  storageMode?: OutputStorageMode | string | null;
  columns?: string[] | null;
  triggerOnUpdate?: boolean | null;
}

/** A link row ("model M reads output O of model S through field F"), written only by historicalModelRunFunction. */
export interface OutputLinkRow {
  id: string;
  processedModelSchemaId: string;
  fieldKey: string;
  outputParamId: string;
  sourceModelId: string;
  sourceStorageMode: OutputStorageMode; // TODO(dead-code #994): always "FILES", never read
  columns: string[];
  triggerOnUpdate: boolean;
  status?: DatasetLinkStatus;
  statusReason?: string | null;
  crossOrg?: boolean | null;
  backfills?: number | null;
}

export const outputLinkId = (modelId: string, fieldKey: string) => `${modelId}#${fieldKey}`;
/** The n-th backfill an output link queued. */
export const outputLinkBackfillId = (modelId: string, fieldKey: string, n: number) => `outlink-${modelId}-${fieldKey}-${n}`;

/**
 * The output links a model should have: one per `model_output` field of its template that the model has filled in.
 * Any model kind may read outputs (a FORECAST model gets them on its schedule; a HISTORICAL one is also run when the
 * upstream output changes). The stored storageMode is only a hint from the wizard: the sync overwrites it with the
 * output's own.
 */
export function outputLinksFor(
  model: { id: string; schemaFields?: Record<string, unknown> | null },
  fieldDefinitions: Record<string, unknown> | null | undefined
): Omit<OutputLinkRow, "status" | "statusReason" | "crossOrg" | "backfills">[] {
  const links: Omit<OutputLinkRow, "status" | "statusReason" | "crossOrg" | "backfills">[] = [];
  for (const [fieldKey, definition] of Object.entries(fieldDefinitions ?? {})) {
    if ((definition as { type?: string } | null)?.type !== "model_output") continue;
    const value = model.schemaFields?.[fieldKey] as ModelOutputFieldValue | null | undefined;
    if (!value || typeof value !== "object" || !value.outputParamId) continue;
    links.push({
      id: outputLinkId(model.id, fieldKey),
      processedModelSchemaId: model.id,
      fieldKey,
      outputParamId: String(value.outputParamId),
      sourceModelId: value.modelId ? String(value.modelId) : "",
      sourceStorageMode: "FILES",
      columns: Array.isArray(value.columns) ? value.columns.map(String) : [],
      triggerOnUpdate: value.triggerOnUpdate !== false,
    });
  }
  return links;
}

export interface OutputSourceRow {
  id: string;
  owner?: string | null;
  organizationId?: string | null;
  modelKind?: string | null;
  deletedAt?: string | null;
}

export interface OutputParamAccessRow {
  id: string;
  schemaId?: string | null;
  storageMode?: string | null;
  active?: boolean | null;
}

/** The storage of a linkable output: FILES (null storageMode is FILES). A DATASET output is refused by canModelReadOutput. */
export const storageModeOf = (_output: Pick<OutputParamAccessRow, "storageMode"> | null | undefined): OutputStorageMode => "FILES";

/**
 * May a model read this output? The same rule as a dataset (canModelReadDataset): the model's owner must be able to
 * read the source model in the app (own it, be in its organization, or be an Admin), and a source outside the model's
 * own organization (or, for a personal model, outside its owner's own models) is crossOrg: allowed for an Admin owner,
 * refused for anyone else. A model never reads its own output; longer loops are caught by wouldLoop.
 */
export function canModelReadOutput(
  model: { id: string; owner?: string | null; organizationId?: string | null },
  ownerIdentity: RequesterIdentity | null,
  source: OutputSourceRow | null | undefined,
  output: OutputParamAccessRow | null | undefined
): { status: DatasetLinkStatus; reason?: string; crossOrg: boolean } {
  if (!output) return { status: "REFUSED", reason: "The output no longer exists", crossOrg: false };
  if (output.active === false) return { status: "REFUSED", reason: "The output is inactive", crossOrg: false };
  if (!source || source.deletedAt) return { status: "REFUSED", reason: "The output's model no longer exists", crossOrg: false };
  if (output.schemaId && output.schemaId !== source.id) {
    return { status: "REFUSED", reason: "The output doesn't belong to that model", crossOrg: false };
  }
  if (source.id === model.id) return { status: "REFUSED", reason: "A model can't read its own output", crossOrg: false };
  if (output.storageMode === "DATASET") {
    return { status: "REFUSED", reason: "This output writes to a dataset; read that dataset instead", crossOrg: false };
  }
  if (!ownerIdentity) return { status: "REFUSED", reason: "The model's owner could not be found", crossOrg: false };
  const ownsSource = ownerMatches(ownerIdentity, source.owner);
  const readable =
    ownsSource ||
    ownerIdentity.groups.includes("Admin") ||
    ownerIdentity.groups.includes("AdminReadOnly") ||
    (Boolean(source.organizationId) && ownerIdentity.groups.includes(source.organizationId as string));
  if (!readable) return { status: "REFUSED", reason: "The model's owner can't read that model", crossOrg: false };
  const crossOrg = model.organizationId ? model.organizationId !== source.organizationId : !ownsSource;
  if (crossOrg && !ownerIdentity.groups.includes("Admin")) {
    return {
      status: "REFUSED",
      reason: model.organizationId
        ? "The output's model isn't in this model's organization; only an Admin can link another organization's model"
        : "A personal model can only read its owner's own models' outputs",
      crossOrg,
    };
  }
  return { status: "ACTIVE", crossOrg };
}

/**
 * Would reading `sourceModelId` make `modelId` read its own output through other models? Walks the sources of the
 * source (`sourcesOf` returns the model ids a model reads), breadth first, up to `maxDepth` hops. Pure apart from the
 * lookup, so a test can feed a fake graph.
 */
export async function wouldLoop(
  modelId: string,
  sourceModelId: string,
  sourcesOf: (id: string) => Promise<string[]>,
  maxDepth = 8
): Promise<{ loop: boolean; hops?: number }> {
  if (sourceModelId === modelId) return { loop: true, hops: 0 };
  const seen = new Set<string>([sourceModelId]);
  let frontier = [sourceModelId];
  for (let hops = 1; hops <= maxDepth && frontier.length; hops += 1) {
    const next: string[] = [];
    for (const id of frontier) {
      for (const upstream of await sourcesOf(id)) {
        if (upstream === modelId) return { loop: true, hops };
        if (!seen.has(upstream)) {
          seen.add(upstream);
          next.push(upstream);
        }
      }
    }
    frontier = next;
  }
  return { loop: false };
}

/** A ForecastRecord (one forecast issue) as a FILES input lists it. */
export interface InputIssue {
  forecastRecordId: string;
  forecastIssued: string;
  s3Key: string;
  format: "csv" | "json";
  seriesStart?: string | null;
  seriesEnd?: string | null;
}

export interface ForecastRecordRow {
  id: string;
  forecastIssued: string;
  s3Key: string;
  seriesStart?: string | null;
  seriesEnd?: string | null;
}

/**
 * The forecast issues a FILES input hands over: the issues made inside the window (a HISTORICAL downstream run), or
 * the latest `latest` ones (a FORECAST downstream run, which passes no window), newest first. Throws if a key is
 * outside the output's own folder.
 */
export function inputIssues(
  rows: ForecastRecordRow[],
  pick: { window: TimeWindow } | { latest: number },
  outputPrefix: string
): InputIssue[] {
  for (const row of rows) {
    if (!outputPrefix || !row.s3Key.startsWith(outputPrefix)) {
      throw new Error(`Input file ${row.id} is outside its source's curated folder (${row.s3Key})`);
    }
  }
  const sorted = [...rows].sort((a, b) => Date.parse(b.forecastIssued) - Date.parse(a.forecastIssued));
  const chosen =
    "window" in pick
      ? sorted.filter((r) => Date.parse(r.forecastIssued) >= Date.parse(pick.window.start) && Date.parse(r.forecastIssued) < Date.parse(pick.window.end))
      : sorted.slice(0, Math.max(1, pick.latest));
  return chosen.map((row) => ({
    forecastRecordId: row.id,
    forecastIssued: toIso(row.forecastIssued),
    s3Key: row.s3Key,
    format: /\.json$/i.test(row.s3Key) ? "json" : "csv",
    seriesStart: row.seriesStart ? toIso(row.seriesStart) : null,
    seriesEnd: row.seriesEnd ? toIso(row.seriesEnd) : null,
  }));
}
